<template>
  <div class="agentic-home-wrapper">
    <ErrorPopup />
    <!-- Main content area with blue gradient background -->
    <div class="home-content">
      <DashboardModeSwitch variant="gradient" :floating="true" />
      <!-- Sidebar inside content -->
      <AgenticSidebar :sidebar-offset="sidebarOffset" />
      
      <!-- Main content section -->
      <div class="main-content-section" :class="{ 'without-chat': !isHomeScreen }">
        <!-- Upper section: Chat input box - Only on home screen -->
        <div v-if="isHomeScreen" class="chat-section">
          <div class="chat-prompt">
            <h1 class="chat-title">Let's create dashboards, {{ userName }} ?</h1>
          </div>
          <ChatInputBox @send-message="handleSendMessage" :loading="loadingDashboard" />
        </div>

        <!-- Lower section: Dashboard overview -->
        <DashboardOverview 
          :dashboards="filteredDashboards"
          :loading="loading"
          :title="overviewTitle"
          :description="overviewDescription"
          @dashboard-click="handleDashboardClick"
          @edit-dashboard="handleDashboardClick"
          @delete-dashboard="openDeleteDialog"
          @star-dashboard="toggleDashboardStar"
          @share-dashboard="toggleDashboardShare"
        />
      </div>
    </div>

    <v-dialog
      transition="dialog-top-transition"
      class="save-dashboard"
      width="600"
      v-model="showDeleteDialog"
    >
      <template v-slot:default="{ isActive }">
        <v-card>
          <v-card-title class="d-flex align-center justify-between">
            <v-spacer></v-spacer>
            <v-btn
              @click="showDeleteDialog = false"
              flat
              icon
              class="chart-preview-close-btn"
              size="x-small"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="mt-4 mx-5">
            <h6 class="text-center">Delete Dashboard</h6>
            <p class="text-center">
              Are you sure you want to delete
              <strong>{{ dashboardPendingDelete?.title || "this dashboard" }}</strong>?
            </p>
          </v-card-text>
          <v-card-actions class="btn-wrap">
            <v-btn class="cancel-btn" @click="isActive.value = false">
              <span>Cancel</span>
            </v-btn>
            <v-btn
              class="delete-btn"
              @click="confirmDeleteDashboard"
              :loading="deletingDashboard"
            >
              <img
                class="mr-2"
                :src="deleteDashboardPopup"
                width="15px"
                height="15px"
                alt="delete_dashboard"
              />
              <span>Delete</span>
            </v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>
  </div>
</template>

<script>
import ChatInputBox from "@/components/AgenticDashboard/ChatInputBox.vue";
import DashboardOverview from "@/components/AgenticDashboard/DashboardOverview.vue";
import AgenticSidebar from "@/components/AgenticDashboard/AgenticSidebar.vue";
import DashboardModeSwitch from "@/components/layout/Dashboard/DashboardModeSwitch.vue";
import ErrorPopup from "@/components/layout/Popups/ErrorPopup.vue";
import { mapGetters } from "vuex";
import api from "@/axiosInterceptor";
import { getTenantPrefix } from "@/utils/tenant";
import screenWidthMixin from "@/assets/JS/screenWidthMixin";
import { eventBus } from "@/eventBus.js";
import {
  getPreviewPlotConfigsFromDashboard,
  getDashboardPlotCount,
} from "@/utils/plotlyMiniTransform";

export default {
  name: "AgenticDashboardHome",
  mixins: [screenWidthMixin],
  components: {
    ChatInputBox,
    DashboardOverview,
    AgenticSidebar,
    DashboardModeSwitch,
    ErrorPopup,
  },
  computed: {
    ...mapGetters("tenantId", ["userData", "getUser"]),
    userName() {
      if (this.getUser?.firstName) {
        return this.getUser.firstName;
      } else if (this.userData?.name) {
        return this.userData.name;
      }
      return "User";
    },
    tenantId() {
      return this.$route.path.split("/")[1] || getTenantPrefix();
    },
    isHomeScreen() {
      return this.$route.name === "AgenticDashboard" && !this.$route.params.dashboardId;
    },
    overviewTitle() {
      const routeName = this.$route.name;
      if (routeName === "AgenticDashboard" && !this.$route.params.dashboardId) {
        return "Home";
      } else if (routeName === "AgenticDashboardAll") {
        return "All dashboards";
      } else if (routeName === "AgenticDashboardStarred") {
        return "Starred";
      } else if (routeName === "AgenticDashboardShared") {
        return "Shared with me";
      }
      return "Dashboards";
    },
    overviewDescription() {
      const routeName = this.$route.name;
      if (routeName === "AgenticDashboard" && !this.$route.params.dashboardId) {
        return "Here you can view and manage your existing dashboards";
      } else if (routeName === "AgenticDashboardAll") {
        return "View all your dashboards including private and public";
      } else if (routeName === "AgenticDashboardStarred") {
        return "Your starred and default dashboards";
      } else if (routeName === "AgenticDashboardShared") {
        return "Dashboards shared with you by others";
      }
      return "Here you can view and manage your existing dashboards";
    },
    sidebarOffset() {
      const baseOffset = this.mainSidebarOpen ? 280 : 64;
      return baseOffset + 8;
    },
    filteredDashboards() {
      if (!this.allDashboards || this.allDashboards.length === 0) {
        return [];
      }

      const routeName = this.$route.name;
      const userEmail = this.getUser?.username;

      let filtered = [];

      const isHomeRoute = routeName === "AgenticDashboard" && !this.$route.params.dashboardId;

      if (isHomeRoute) {
        filtered = this.allDashboards.slice(0, 8);
      } else if (routeName === "AgenticDashboardAll") {
        filtered = this.allDashboards;
      } else if (routeName === "AgenticDashboardStarred") {
        filtered = this.allDashboards.filter(
          (dashboard) => dashboard.isDefault || dashboard.isStarred
        );
      } else if (routeName === "AgenticDashboardShared") {
        filtered = this.allDashboards.filter(
          (dashboard) =>
            dashboard.AccessLevel === "public" ||
            (dashboard.AccessLevel === "shared" && dashboard.createdBy !== userEmail)
        );
      } else {
        filtered = this.allDashboards.slice(0, 8);
      }

      return filtered.map((dashboard) => {
        const plotCount = getDashboardPlotCount(dashboard);
        return {
          id: dashboard.ID,
          title: dashboard.name,
          plotCount,
          maxPlots: 6,
          isStarred: dashboard.isDefault || dashboard.isStarred || false,
          isOwner: this.isDashboardOwner(dashboard),
          canStar: this.canStarDashboard(dashboard),
          isPublic: dashboard.AccessLevel === "public",
          shared:
            dashboard.AccessLevel === "public" || dashboard.AccessLevel === "shared",
          previewPlotConfigs: getPreviewPlotConfigsFromDashboard(dashboard, 4),
          dashboard,
        };
      });
    },
  },
  data() {
    return {
      allDashboards: [],
      loading: false,
      loadingDashboard: false,
      mainSidebarOpen: false,
      sidebarObserver: null,
      showDeleteDialog: false,
      deletingDashboard: false,
      dashboardPendingDelete: null,
      deleteDashboardPopup: require("@/assets/Svg/Chart/delete-white.svg"),
    };
  },
  methods: {
    isDashboardOwner(dashboard) {
      const userEmail = this.getUser?.username;
      if (!userEmail || !dashboard) return false;
      const creator = dashboard.createdBy || dashboard.CreatedBy || "";
      return creator === userEmail;
    },
    canStarDashboard(dashboard) {
      if (!dashboard) return false;
      if (this.isDashboardOwner(dashboard)) return true;
      const level = dashboard.AccessLevel || "";
      return level === "private" || level === "personal";
    },
    toggleDashboardShare(cardDashboard, isPublic) {
      const original = cardDashboard?.dashboard || cardDashboard;
      const dashboardId = cardDashboard?.id || original?.ID;
      const dashboardName = original?.name || cardDashboard?.title;
      if (!dashboardId || !dashboardName || !cardDashboard?.isOwner) return;

      const accessLevel = isPublic ? "public" : "private";
      const current = this.allDashboards.find((item) => item.ID === dashboardId);
      const previousLevel = current?.AccessLevel || "private";

      if (current) {
        current.AccessLevel = accessLevel;
      }

      api
        .put(
          `public/api/dashboards/${dashboardId}`,
          {
            Data: {
              dashboard: {
                name: dashboardName,
                AccessLevel: accessLevel,
              },
            },
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "X-TenantID": this.tenantId,
            },
          }
        )
        .then((response) => {
          if (
            response &&
            response.data &&
            (response.data.statusCode === 200 || response.data.statusCode === 201)
          ) {
            this.$store.commit("api/showSnackbar", {
              message: isPublic
                ? "Dashboard is now shared"
                : "Dashboard is now private",
              color: "success",
              timeout: 3000,
            });
          } else {
            if (current) current.AccessLevel = previousLevel;
            this.$store.commit("api/showSnackbar", {
              message: response?.data?.message || "Failed to update dashboard visibility",
              color: "error",
              timeout: 5000,
            });
          }
        })
        .catch((error) => {
          console.error("Error updating dashboard visibility:", error);
          if (current) current.AccessLevel = previousLevel;
          this.$store.commit("api/showSnackbar", {
            message: error.response?.data?.message || "Error updating dashboard visibility",
            color: "error",
            timeout: 5000,
          });
        });
    },
    toggleDashboardStar(cardDashboard) {
      const original = cardDashboard?.dashboard || cardDashboard;
      const dashboardId = cardDashboard?.id || original?.ID;
      const dashboardName = original?.name || cardDashboard?.title;
      if (!dashboardId || !dashboardName) return;

      const current = this.allDashboards.find((item) => item.ID === dashboardId);
      if (!current) return;

      const isCurrentlyStarred = Boolean(current.isDefault || current.isStarred);
      const nextStarred = !isCurrentlyStarred;

      if (nextStarred) {
        this.allDashboards.forEach((item) => {
          if (item.ID !== dashboardId) {
            item.isDefault = false;
            item.isStarred = false;
          }
        });
      }

      current.isDefault = nextStarred;
      current.isStarred = nextStarred;

      api
        .put(
          `public/api/dashboards/${dashboardId}`,
          {
            Data: {
              dashboard: {
                name: dashboardName,
                isDefault: nextStarred,
              },
            },
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "X-TenantID": this.tenantId,
            },
          }
        )
        .then((response) => {
          if (
            response &&
            response.data &&
            (response.data.statusCode === 200 || response.data.statusCode === 201)
          ) {
            if (nextStarred) {
              localStorage.setItem("defaultDashboard", dashboardName);
            } else {
              const savedDefault = localStorage.getItem("defaultDashboard");
              if (savedDefault === dashboardName) {
                localStorage.removeItem("defaultDashboard");
              }
            }
            this.$store.commit("api/showSnackbar", {
              message: nextStarred
                ? "Dashboard added to starred"
                : "Dashboard removed from starred",
              color: "success",
              timeout: 3000,
            });
          } else {
            this.fetchDashboards();
            this.$store.commit("api/showSnackbar", {
              message: response?.data?.message || "Failed to update starred status",
              color: "error",
              timeout: 5000,
            });
          }
        })
        .catch((error) => {
          console.error("Error updating starred dashboard:", error);
          this.fetchDashboards();
          this.$store.commit("api/showSnackbar", {
            message: error.response?.data?.message || "Error updating starred status",
            color: "error",
            timeout: 5000,
          });
        });
    },
    openDeleteDialog(dashboard) {
      if (!dashboard?.isOwner) return;
      this.dashboardPendingDelete = dashboard;
      this.showDeleteDialog = true;
    },
    confirmDeleteDashboard() {
      const dashboardId =
        this.dashboardPendingDelete?.id ||
        this.dashboardPendingDelete?.dashboard?.ID;
      if (!dashboardId) {
        this.$store.commit("api/showSnackbar", {
          message: "Dashboard ID is missing",
          color: "error",
          timeout: 5000,
        });
        return;
      }

      this.deletingDashboard = true;
      api
        .delete(`public/api/dashboards/${dashboardId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-TenantID": this.tenantId,
          },
        })
        .then((response) => {
          if (
            response &&
            response.data &&
            (response.data.statusCode === 200 || response.data.statusCode === 201)
          ) {
            this.allDashboards = this.allDashboards.filter(
              (item) => item.ID !== dashboardId
            );
            this.$store.commit("api/showSnackbar", {
              message: "Dashboard deleted successfully",
              color: "success",
              timeout: 3000,
            });
            this.showDeleteDialog = false;
            this.dashboardPendingDelete = null;
          } else {
            this.$store.commit("api/showSnackbar", {
              message: response?.data?.message || "Failed to delete dashboard",
              color: "error",
              timeout: 5000,
            });
          }
        })
        .catch((error) => {
          console.error("Error deleting dashboard:", error);
          this.$store.commit("api/showSnackbar", {
            message: error.response?.data?.message || "Error deleting dashboard",
            color: "error",
            timeout: 5000,
          });
        })
        .finally(() => {
          this.deletingDashboard = false;
        });
    },
    async handleSendMessage(message) {
      const llmUrl = process.env.VUE_APP_LLM_URL;
      if (!llmUrl) {
        console.error("VUE_APP_LLM_URL is not defined in .env");
        this.$store.commit('api/showSnackbar', {
          message: "LLM URL is not configured",
          color: 'error',
          timeout: 5000
        });
        return;
      }

      this.loadingDashboard = true;
      try {
        const response = await api.post(
          `${llmUrl}api/dashboard`,
          { message: message },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "X-TenantID": this.tenantId,
            },
          }
        );

        if (response && response.data) {
          const dashboardData = response.data;
          this.$store.commit('api/showSnackbar', {
            message: "Dashboard created successfully",
            color: 'success',
            timeout: 3000
          });
          const dashboardName = dashboardData.Name || dashboardData.name || "Untitled Dashboard";
          this.$router.push({
            name: "AgenticDashboardView",
            params: { dashboardName: dashboardName },
            query: { 
              message: message,
              dashboardData: JSON.stringify(dashboardData)
            },
          });
        } else {
          console.error("Invalid response from API");
          this.$store.commit('api/showSnackbar', {
            message: "Failed to create dashboard",
            color: 'error',
            timeout: 5000
          });
        }
      } catch (error) {
        console.error("Error creating dashboard:", error);
        this.$store.commit('api/showSnackbar', {
          message: error.response?.data?.message || "Failed to create dashboard",
          color: 'error',
          timeout: 5000
        });
      } finally {
        this.loadingDashboard = false;
      }
    },
    handleDashboardClick(dashboard) {
      // Navigate to the specific dashboard using dashboard name
      // The dashboard object can be either:
      // 1. The mapped object with 'title' property
      // 2. The original dashboard object with 'name' or 'Name' property
      // 3. The mapped object with original dashboard in 'dashboard' property
      let dashboardName = "Untitled Dashboard";
      
      if (dashboard.dashboard) {
        // If original dashboard object is nested
        dashboardName = dashboard.dashboard.name || dashboard.dashboard.Name || dashboard.title || dashboardName;
      } else if (dashboard.name || dashboard.Name) {
        // If it's the original dashboard object
        dashboardName = dashboard.name || dashboard.Name;
      } else if (dashboard.title) {
        // If it's the mapped object with title
        dashboardName = dashboard.title;
      }
      
      // Navigate to dashboard view with name in URL
      this.$router.push({
        name: "AgenticDashboardView",
        params: { dashboardName: dashboardName },
      });
    },
    fetchDashboards() {
      this.loading = true;
      api
        .get("public/api/dashboards", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-TenantID": this.tenantId,
          },
        })
        .then((response) => {
          if (
            response &&
            response.data &&
            (response.data.statusCode === 200 || response.data.statusCode === 201)
          ) {
            const dashboards = response.data.data.dashboards || [];
            // Sort dashboards by creation date (most recent first)
            // Check for CreatedOn, Created, or UpdatedOn fields
            this.allDashboards = dashboards.sort((a, b) => {
              const dateA = a.CreatedOn || a.Created || a.UpdatedOn || a.Updated || a.createdOn || a.created || 0;
              const dateB = b.CreatedOn || b.Created || b.UpdatedOn || b.Updated || b.createdOn || b.created || 0;
              
              // Convert to timestamps for comparison
              const timestampA = dateA ? new Date(dateA).getTime() : 0;
              const timestampB = dateB ? new Date(dateB).getTime() : 0;
              
              // Sort descending (most recent first)
              return timestampB - timestampA;
            });
          } else {
            console.error("Error fetching dashboards:", response?.data?.message);
            this.allDashboards = [];
          }
        })
        .catch((error) => {
          console.error("Error fetching dashboards:", error);
          this.allDashboards = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    checkSidebarState() {
      if (this.screenWidth && this.screenWidth >= 768) {
        const sidebar = document.querySelector('.sidebar');
        if (sidebar) {
          this.mainSidebarOpen = !sidebar.classList.contains('close');
        } else {
          this.$nextTick(() => {
            const sidebar = document.querySelector('.sidebar');
            if (sidebar) {
              this.mainSidebarOpen = !sidebar.classList.contains('close');
            }
          });
        }
      } else {
        this.mainSidebarOpen = false;
      }
    },
    setupSidebarObserver() {
      this.$nextTick(() => {
        const sidebar = document.querySelector('.sidebar');
        if (sidebar) {
          this.sidebarObserver = new MutationObserver(() => {
            this.mainSidebarOpen = !sidebar.classList.contains('close');
          });
          this.sidebarObserver.observe(sidebar, {
            attributes: true,
            attributeFilter: ['class']
          });
        }
      });
    },
    handleDrawerStateChange(isOpen) {
      this.mainSidebarOpen = isOpen;
    },
  },
  watch: {
    "$route"(to) {
      // Refetch when route changes to update filtered dashboards
      if (
        to.name === "AgenticDashboard" ||
        to.name === "AgenticDashboardAll" ||
        to.name === "AgenticDashboardStarred" ||
        to.name === "AgenticDashboardShared"
      ) {
        this.fetchDashboards();
      }
    },
  },
  mounted() {
    this.fetchDashboards();
    this.checkSidebarState();
    eventBus.on("mainSidebarStateChanged", this.handleDrawerStateChange);
    
    this.setupSidebarObserver();
  },
  beforeUnmount() {
    eventBus.off("mainSidebarStateChanged", this.handleDrawerStateChange);
    if (this.sidebarObserver) {
      this.sidebarObserver.disconnect();
    }
  },
};
</script>

<style scoped>
@import '../../assets/Styling/agenticDashboard/home.css';
</style>

